import { useCreate, useDelete, useDetail, useList, useUpdate, usePartialUpdate, useAction, fetchApi } from "@inmagik/react-crud"
import type { Media } from "../types"
import { API_URL, PAGE_SIZE } from "../constants"
import { useAuth } from "../auth"
import { useCallback, useMemo } from "react"
import { normalizeParams } from "@inmagik/react-crud/dist/crud/utils"

export function useMediaList(params: Record<string, string> = {}) {
  return useList<Media>(`${API_URL}/admin/media`, {
    query: params,
  })
}

export function useMedia(id: number | undefined) {
  return useDetail<Media>(`${API_URL}/admin/media`, id?.toString())
}

export function useCreateMedia() {
  return useCreate<Media>(`${API_URL}/admin/media`, {
    encode: "formdata",
  })
}

export function useUpdateMedia() {
  return useUpdate<Media>(`${API_URL}/admin/media`, {
    encode: "formdata",
  })
}

export function useDeleteMedia() {
  return useDelete(`${API_URL}/admin/media`)
}

export function usePartialUpdateMedia() {
  return usePartialUpdate<Media>(`${API_URL}/admin/media`)
}

export function useDeleteMultiMedia() {
  return useAction(`${API_URL}/admin/media/delete_multi/`, {
    method: "POST",
    responseType: "text",
  })
}

export function useMediaLazy() {
  const auth = useAuth()
  return useCallback((id: number) => {
    return fetchApi(`${API_URL}/admin/media/${id}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<Media>
  }, [])
}

export function useMediaListLazy() {
  const auth = useAuth()
  return useCallback((params: Record<string, string | number | Array<number>>) => {
    const qs = normalizeParams(params).toString()
    return fetchApi(`${API_URL}/admin/media?${qs}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<{ results: Media[] }>
  }, [])
}



export function useOnlyMediaResults(filters: { search: string; page: number; ordering: string }) {
  const { data } = useMediaList({
    search: filters.search,
    page: String(filters.page),
    ordering: filters.ordering,
  })

  const results = useMemo(() => {
    const r = data?.results ?? []
    return r
  }, [data])

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil((data.count ?? 0) / PAGE_SIZE)
  }, [data])

  return { results, numPages }
}
